import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import type { HeadingStyle } from '@/lib/patientReports/template';

/**
 * Fill a Word layout. The stored .docx keeps placeholder tokens.
 * Values are written only into the bytes returned from here.
 */

export const DOCX_TEMPLATE_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const MAX_DOCX_BYTES = 20 * 1024 * 1024;
const WORD_PART = /^word\/(document|footnotes|endnotes|header\d+|footer\d+)\.xml$/i;
const PARAGRAPH_RE = /<w:p\b[^>]*>[\s\S]*?<\/w:p>/g;
const TEXT_RE = /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/g;
const PIECE_RE =
  /<w:hyperlink\b[^>]*>[\s\S]*?<\/w:hyperlink>|<w:sdt\b[^>]*>[\s\S]*?<\/w:sdt>|<w:r\b[^>]*\/>|<w:r\b[^>]*>[\s\S]*?<\/w:r>|<w:proofErr\b[^>]*\/>|<w:bookmarkStart\b[^>]*\/>|<w:bookmarkEnd\b[^>]*\/>/g;

const CPR_IN_TEMPLATE = /\d{6}-\d{4}/;

/** A4 width in twips. Same number as the example file's page size. */
const A4_WIDTH_TWIPS = 11906;
/** 2.5 cm. Top and bottom of the example stay at this. */
const EXAMPLE_VERTICAL_MARGIN_TWIPS = 1417;
/** About one third of the previous 2.5 cm side margin. */
const EXAMPLE_HORIZONTAL_MARGIN_TWIPS = Math.round(EXAMPLE_VERTICAL_MARGIN_TWIPS / 3);
/** Distance from the left margin to the right margin, where a right tab sits. */
const EXAMPLE_CONTENT_WIDTH_TWIPS = A4_WIDTH_TWIPS - EXAMPLE_HORIZONTAL_MARGIN_TWIPS * 2;
/** Right tab for a part that has no page margins of its own. */
const FALLBACK_RIGHT_TAB_TWIPS = 9026;

export type TocEntry = {
  /** Heading the line jumps to. */
  title: string;
  /** Text in front of the page number. May start with a number or a letter. */
  label: string;
  /** Heading style for this line. Missing means bold. */
  style?: HeadingStyle;
};

export type ContentPiece =
  | { type: 'text'; text: string }
  | { type: 'heading'; text: string; style: HeadingStyle }
  | { type: 'toc'; heading: string; headingStyle?: HeadingStyle; entries: readonly TocEntry[] };

export type PlaceholderReplacement =
  | { type: 'text'; text: string }
  | { type: 'field'; instruction: 'PAGE' | 'NUMPAGES' }
  | { type: 'toc'; entries: readonly TocEntry[] }
  | { type: 'flow'; pieces: readonly ContentPiece[] };

export type PlaceholderDictionary = ReadonlyMap<string, PlaceholderReplacement>;

type Piece =
  | { kind: 'text'; xml: string; rPr: string; text: string }
  | { kind: 'skip'; xml: string }
  | { kind: 'opaque'; xml: string }
  | { kind: 'gap'; xml: string };

export function packDocx(files: Record<string, string>): Uint8Array {
  const entries: Record<string, Uint8Array> = {};
  for (const [name, xml] of Object.entries(files)) entries[name] = strToU8(xml);
  return zipSync(entries);
}

export function assertDocxTemplate(bytes: Uint8Array): void {
  if (bytes.byteLength === 0 || bytes.byteLength > MAX_DOCX_BYTES) {
    throw new Error('docx-type');
  }
  if (bytes[0] !== 0x50 || bytes[1] !== 0x4b) throw new Error('docx-type');
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes);
  } catch {
    throw new Error('docx-type');
  }
  const names = Object.keys(files).map((name) => name.replace(/\\/g, '/'));
  if (!names.some((name) => name === 'word/document.xml')) throw new Error('docx-type');
  if (CPR_IN_TEMPLATE.test(docxVisibleText(bytes))) throw new Error('docx-cpr');
}

export async function readDocxTemplateFile(file: File): Promise<Uint8Array> {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.docx') && file.type !== DOCX_TEMPLATE_MIME) throw new Error('docx-type');
  if (file.size <= 0 || file.size > MAX_DOCX_BYTES) throw new Error('docx-type');
  const bytes = new Uint8Array(await file.arrayBuffer());
  assertDocxTemplate(bytes);
  return bytes;
}

export function docxVisibleText(bytes: Uint8Array): string {
  const files = unzipSync(bytes);
  const chunks: string[] = [];
  for (const [name, data] of Object.entries(files)) {
    const path = name.replace(/\\/g, '/');
    if (!WORD_PART.test(path)) continue;
    const xml = strFromU8(data);
    for (const paragraph of xml.matchAll(PARAGRAPH_RE)) {
      chunks.push(paragraphText(paragraph[0]));
    }
  }
  return chunks.join('\n');
}

export function docxHasToken(bytes: Uint8Array, token: string): boolean {
  return docxVisibleText(bytes).includes(token);
}

/** Returns a new docx. Does not mutate `template`. */
export function fillDocxTemplate(
  template: Uint8Array,
  dictionary: PlaceholderDictionary,
): Uint8Array {
  const files = unzipSync(template.slice());
  const next: Record<string, Uint8Array> = {};
  for (const [name, data] of Object.entries(files)) {
    const path = name.replace(/\\/g, '/');
    if (!WORD_PART.test(path)) {
      next[path] = data;
      continue;
    }
    const xml = fillWordXml(strFromU8(data), dictionary);
    next[path] = strToU8(xml);
  }
  const settings = next['word/settings.xml'];
  if (settings) next['word/settings.xml'] = strToU8(withUpdateFields(strFromU8(settings)));
  return zipSync(next);
}

const TIGHT_LINE = '<w:spacing w:before="0" w:after="0" w:line="200" w:lineRule="auto"/>';

export function buildExampleWordTemplate(): Uint8Array {
  const header = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:p>
    <w:pPr>${TIGHT_LINE}<w:tabs><w:tab w:val="right" w:pos="${EXAMPLE_CONTENT_WIDTH_TWIPS}"/></w:tabs></w:pPr>
    <w:r><w:t>[Patientnavn]</w:t></w:r>
    <w:r><w:tab/></w:r>
    <w:r><w:t>[Rapportdato]</w:t></w:r>
  </w:p>
  <w:p>
    <w:pPr>${TIGHT_LINE}</w:pPr>
    <w:r><w:t>[CPR-nr.]</w:t></w:r>
  </w:p>
</w:hdr>`;
  const footer = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:ftr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:p>
    <w:pPr><w:jc w:val="center"/></w:pPr>
    <w:r><w:t>[Sidetal]/[Total sider]</w:t></w:r>
  </w:p>
</w:ftr>`;
  const document = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    <w:p>
      <w:r><w:rPr><w:b/><w:sz w:val="36"/></w:rPr><w:t>[Rapportnavn]</w:t></w:r>
    </w:p>
    <w:p><w:r><w:t>[Behandler]</w:t></w:r></w:p>
    <w:p><w:r><w:rPr><w:b/></w:rPr><w:t>[Indholdsfortegnelse]</w:t></w:r></w:p>
    <w:p><w:r><w:t>[Diagnose]</w:t></w:r></w:p>
    <w:p><w:r><w:t>[Indhold]</w:t></w:r></w:p>
    <w:sectPr>
      <w:headerReference w:type="default" r:id="rId3"/>
      <w:footerReference w:type="default" r:id="rId4"/>
      <w:pgSz w:w="11906" w:h="16838"/>
      <w:pgMar w:top="${EXAMPLE_VERTICAL_MARGIN_TWIPS}" w:right="${EXAMPLE_HORIZONTAL_MARGIN_TWIPS}" w:bottom="${EXAMPLE_VERTICAL_MARGIN_TWIPS}" w:left="${EXAMPLE_HORIZONTAL_MARGIN_TWIPS}" w:header="709" w:footer="709" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`;

  return packDocx({
    '[Content_Types].xml': `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>
  <Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/>
  <Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/>
</Types>`,
    '_rels/.rels': `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
    'word/_rels/document.xml.rels': `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header1.xml"/>
  <Relationship Id="rId4" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/>
</Relationships>`,
    'word/styles.xml': `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/></w:rPr></w:rPrDefault></w:docDefaults>
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/></w:style>
</w:styles>`,
    'word/settings.xml': `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:updateFields w:val="true"/>
</w:settings>`,
    'word/header1.xml': header,
    'word/footer1.xml': footer,
    'word/document.xml': document,
  });
}

function fillWordXml(xml: string, dictionary: PlaceholderDictionary): string {
  if (!dictionary.size || !xml.includes('<w:p')) return xml;
  const flow = dictionary.get('[Indhold]');
  const suppressTocToken =
    flow?.type === 'flow' &&
    flow.pieces.some((piece) => piece.type === 'toc') &&
    xml.includes('[Indhold]');
  const rightTab = rightContentTwips(xml);
  return xml.replace(PARAGRAPH_RE, (paragraph) =>
    fillParagraph(paragraph, dictionary, suppressTocToken, rightTab),
  );
}

function fillParagraph(
  paragraph: string,
  dictionary: PlaceholderDictionary,
  suppressTocToken: boolean,
  rightTab: number,
): string {
  const visible = paragraphText(paragraph);
  if (!containsKnownToken(visible, dictionary)) return paragraph;
  const content = dictionary.get('[Indhold]');
  if (content?.type === 'flow' && visible.trim() === '[Indhold]') {
    return flowParagraphs(paragraph, content.pieces, rightTab);
  }
  const toc = dictionary.get('[Indholdsfortegnelse]');
  if (toc?.type === 'toc' && visible.trim() === '[Indholdsfortegnelse]') {
    if (suppressTocToken) return emptyParagraph(paragraph);
    return tocParagraphs(paragraph, toc.entries, rightTab);
  }
  const open = paragraph.match(/^<w:p\b[^>]*>/)?.[0] ?? '<w:p>';
  let inner = paragraph.slice(open.length, paragraph.length - '</w:p>'.length);
  const pPr = inner.match(/^\s*<w:pPr>[\s\S]*?<\/w:pPr>/)?.[0] ?? '';
  if (pPr) inner = inner.slice(pPr.length);
  return `${open}${pPr}${fillInner(inner, dictionary)}</w:p>`;
}

function fillInner(inner: string, dictionary: PlaceholderDictionary): string {
  const pieces = piecesOf(inner);
  let out = '';
  let group: Piece[] = [];
  const flush = () => {
    if (group.length === 0) return;
    const texts = group.filter(
      (piece): piece is Extract<Piece, { kind: 'text' }> => piece.kind === 'text',
    );
    const joined = texts.map((piece) => piece.text).join('');
    if (texts.length > 0 && containsKnownToken(joined, dictionary)) {
      out += renderReplacement(joined, texts[0]?.rPr ?? '', dictionary);
    } else {
      out += group.map((piece) => piece.xml).join('');
    }
    group = [];
  };
  for (const piece of pieces) {
    if (piece.kind === 'opaque' || (piece.kind === 'gap' && piece.xml.trim() !== '')) {
      flush();
      out += piece.xml;
      continue;
    }
    group.push(piece);
  }
  flush();
  return out;
}

function piecesOf(inner: string): Piece[] {
  const pieces: Piece[] = [];
  const re = new RegExp(PIECE_RE.source, 'g');
  let last = 0;
  for (const match of inner.matchAll(re)) {
    const index = match.index ?? 0;
    if (index > last) pieces.push({ kind: 'gap', xml: inner.slice(last, index) });
    pieces.push(classify(match[0]));
    last = index + match[0].length;
  }
  if (last < inner.length) pieces.push({ kind: 'gap', xml: inner.slice(last) });
  return pieces;
}

function classify(xml: string): Piece {
  if (xml.startsWith('<w:proofErr') || xml.startsWith('<w:bookmark')) {
    return { kind: 'skip', xml };
  }
  if (!xml.startsWith('<w:r')) return { kind: 'opaque', xml };
  if (/<w:(drawing|pict|object|tab|br|cr|sym|fldChar|instrText|ptab)\b/.test(xml)) {
    return { kind: 'opaque', xml };
  }
  const texts = [...xml.matchAll(TEXT_RE)];
  if (texts.length === 0) return { kind: 'skip', xml };
  const rPr = xml.match(/<w:rPr>[\s\S]*?<\/w:rPr>/)?.[0] ?? '';
  return {
    kind: 'text',
    xml,
    rPr,
    text: texts.map((part) => decodeXml(part[1] ?? '')).join(''),
  };
}

function renderReplacement(text: string, rPr: string, dictionary: PlaceholderDictionary): string {
  return segmentText(text, dictionary)
    .map((segment) =>
      segment.type === 'text' ? textRun(segment.text, rPr) : fieldRuns(segment.instruction, rPr),
    )
    .join('');
}

function segmentText(
  text: string,
  dictionary: PlaceholderDictionary,
): Array<{ type: 'text'; text: string } | { type: 'field'; instruction: 'PAGE' | 'NUMPAGES' }> {
  const tokens = [...dictionary.keys()].sort((a, b) => b.length - a.length);
  const out: Array<
    { type: 'text'; text: string } | { type: 'field'; instruction: 'PAGE' | 'NUMPAGES' }
  > = [];
  let buf = '';
  for (let index = 0; index < text.length; ) {
    const token =
      text[index] === '[' ? tokens.find((item) => text.startsWith(item, index)) : undefined;
    if (!token) {
      buf += text[index];
      index += 1;
      continue;
    }
    const value = dictionary.get(token);
    if (buf) {
      out.push({ type: 'text', text: buf });
      buf = '';
    }
    if (value?.type === 'field') out.push(value);
    else if (value?.type === 'toc') buf = value.entries.map((entry) => entry.label).join('\n');
    else if (value?.type === 'flow') buf = flattenPieces(value.pieces);
    else if (value?.type === 'text' && value.text) buf = value.text;
    index += token.length;
  }
  if (buf) out.push({ type: 'text', text: buf });
  return out;
}

function emptyParagraph(paragraph: string): string {
  const open = paragraph.match(/^<w:p\b[^>]*>/)?.[0] ?? '<w:p>';
  const inner = paragraph.slice(open.length, paragraph.length - '</w:p>'.length);
  const pPr = inner.match(/^\s*<w:pPr>[\s\S]*?<\/w:pPr>/)?.[0] ?? '';
  return `${open}${pPr}</w:p>`;
}

/**
 * Section text, with a contents list where that section stands.
 * A page break stays a break inside the text pieces.
 * Headings use the section's style. Body text keeps the paragraph's own formatting.
 */
function flowParagraphs(
  paragraph: string,
  pieces: readonly ContentPiece[],
  rightTab: number,
): string {
  const open = paragraph.match(/^<w:p\b[^>]*>/)?.[0] ?? '<w:p>';
  const inner = paragraph.slice(open.length, paragraph.length - '</w:p>'.length);
  const pPr = inner.match(/^\s*<w:pPr>[\s\S]*?<\/w:pPr>/)?.[0] ?? '';
  const rPr = paragraph.match(/<w:rPr>[\s\S]*?<\/w:rPr>/)?.[0] ?? '';
  const out: string[] = [];
  let spans: { text: string; style: HeadingStyle | null }[] = [];
  const flush = () => {
    const runs = styledTextRuns(spans, rPr);
    if (runs) out.push(`${open}${pPr}${runs}</w:p>`);
    spans = [];
  };
  for (const piece of pieces) {
    if (piece.type === 'text') {
      spans.push({ text: piece.text, style: null });
      continue;
    }
    if (piece.type === 'heading') {
      spans.push({ text: piece.text, style: piece.style });
      continue;
    }
    flush();
    if (piece.heading.trim()) {
      const runs = textRun(
        piece.heading.trim(),
        headingRunProps(rPr, piece.headingStyle ?? 'bold'),
      );
      if (runs) out.push(`${open}${pPr}${runs}</w:p>`);
    }
    if (piece.entries.length > 0) out.push(tocParagraphs(paragraph, piece.entries, rightTab));
  }
  flush();
  return out.join('') || emptyParagraph(paragraph);
}

function flattenPieces(pieces: readonly ContentPiece[]): string {
  return pieces
    .map((piece) => {
      if (piece.type === 'text' || piece.type === 'heading') return piece.text;
      return [piece.heading.trim(), ...piece.entries.map((entry) => entry.label)]
        .filter(Boolean)
        .join('\n');
    })
    .join('');
}

/** One paragraph per topic: label, right-aligned tab, page-number field. */
function tocParagraphs(paragraph: string, entries: readonly TocEntry[], rightTab: number): string {
  const open = paragraph.match(/^<w:p\b[^>]*>/)?.[0] ?? '<w:p>';
  const inner = paragraph.slice(open.length, paragraph.length - '</w:p>'.length);
  const pPr = inner.match(/^\s*<w:pPr>[\s\S]*?<\/w:pPr>/)?.[0] ?? '';
  const rPr = paragraph.match(/<w:rPr>[\s\S]*?<\/w:rPr>/)?.[0] ?? '';
  const props = ensureRightTab(pPr, rightTab);
  if (entries.length === 0) return emptyParagraph(paragraph);
  return entries
    .map((entry) => {
      const label = encodeXml(entry.label);
      const instruction = encodeXml(tocInstruction(entry));
      const labelProps = tocEntryRunProps(rPr);
      return (
        `${open}${props}` +
        `<w:r>${labelProps}<w:t xml:space="preserve">${label}</w:t></w:r>` +
        `<w:r><w:tab/></w:r>` +
        `<w:r><w:fldChar w:fldCharType="begin"/></w:r>` +
        `<w:r><w:instrText xml:space="preserve">${instruction}</w:instrText></w:r>` +
        `<w:r><w:fldChar w:fldCharType="separate"/></w:r>` +
        `<w:r><w:fldChar w:fldCharType="end"/></w:r>` +
        `</w:p>`
      );
    })
    .join('');
}

/** Bare `TOC` keeps the visible label as the link target. A title is the heading. */
function tocInstruction(entry: TocEntry): string {
  if (entry.label === entry.title) return ' TOC ';
  return ` TOC ${entry.title} `;
}

/**
 * Twips from the left margin to the right margin.
 * The contents page number uses this so it stays on the right edge
 * when the side margins change. A part without page margins keeps the
 * previous stop.
 */
function rightContentTwips(xml: string): number {
  const margins = [...xml.matchAll(/<w:pgMar\b[^>]*>/g)];
  const margin = margins[margins.length - 1]?.[0];
  if (!margin) return FALLBACK_RIGHT_TAB_TWIPS;
  const pages = [...xml.matchAll(/<w:pgSz\b[^>]*>/g)];
  const page = pages[pages.length - 1]?.[0];
  const num = (tag: string | undefined, name: string, fallback: number) => {
    const raw = tag?.match(new RegExp(`\\bw:${name}="(-?\\d+)"`))?.[1];
    if (!raw) return fallback;
    const value = Number(raw);
    return Number.isFinite(value) ? value : fallback;
  };
  const width = num(page, 'w', A4_WIDTH_TWIPS);
  return Math.max(0, width - num(margin, 'left', 0) - num(margin, 'right', 0));
}

function ensureRightTab(pPr: string, rightTab: number): string {
  if (/<w:tab\b[^>]*w:val="right"/.test(pPr)) return pPr;
  const tab = `<w:tabs><w:tab w:val="right" w:leader="dot" w:pos="${rightTab}"/></w:tabs>`;
  if (!pPr) return `<w:pPr>${tab}</w:pPr>`;
  return pPr.replace('</w:pPr>', `${tab}</w:pPr>`);
}

/**
 * A contents line is ordinary text. The section heading style, and a bold
 * placeholder run, must not make the line bold or italic.
 */
function tocEntryRunProps(baseRPr: string): string {
  const stripped = stripToggle(baseRPr);
  return `<w:rPr><w:b w:val="0"/><w:i w:val="0"/>${stripped}</w:rPr>`;
}

/** Bold, italic, or both, without keeping a conflicting toggle from the template run. */
function headingRunProps(baseRPr: string, style: HeadingStyle): string {
  const stripped = stripToggle(baseRPr);
  const marks = style === 'italic' ? '<w:i/>' : style === 'both' ? '<w:b/><w:i/>' : '<w:b/>';
  return `<w:rPr>${marks}${stripped}</w:rPr>`;
}

function stripToggle(baseRPr: string): string {
  const inner = baseRPr.replace(/^<w:rPr>/, '').replace(/<\/w:rPr>$/, '');
  return inner
    .replace(/<w:bCs\b[^>]*\/>/g, '')
    .replace(/<w:iCs\b[^>]*\/>/g, '')
    .replace(/<w:b\b[^>]*\/>/g, '')
    .replace(/<w:i\b[^>]*\/>/g, '');
}

/**
 * Same line breaks and page breaks as `textRun`, but a heading can use its own style.
 * Body spans pass a null style and keep `baseRPr`.
 */
function styledTextRuns(
  spans: readonly { text: string; style: HeadingStyle | null }[],
  baseRPr: string,
): string {
  let text = '';
  const styles: (HeadingStyle | null)[] = [];
  for (const span of spans) {
    const clean = stripControls(span.text);
    text += clean;
    for (let index = 0; index < clean.length; index += 1) styles.push(span.style);
  }
  if (!text) return '';
  const pages: { text: string; styles: (HeadingStyle | null)[] }[] = [];
  let start = 0;
  for (let index = 0; index <= text.length; index += 1) {
    if (index === text.length || text[index] === '\f') {
      pages.push({ text: text.slice(start, index), styles: styles.slice(start, index) });
      start = index + 1;
    }
  }
  return pages
    .map((page, pageIndex) => {
      let source = page.text;
      let sourceStyles = page.styles;
      if (pageIndex > 0) {
        const skip = /^\n+/.exec(source)?.[0].length ?? 0;
        source = source.slice(skip);
        sourceStyles = sourceStyles.slice(skip);
      }
      const runs = linesToRuns(source, sourceStyles, baseRPr);
      const pageBreak = pageIndex < pages.length - 1 ? '<w:r><w:br w:type="page"/></w:r>' : '';
      return runs + pageBreak;
    })
    .join('');
}

function linesToRuns(
  source: string,
  styles: readonly (HeadingStyle | null)[],
  baseRPr: string,
): string {
  const parts: { text: string; styles: readonly (HeadingStyle | null)[] }[] = [];
  let start = 0;
  for (let index = 0; index <= source.length; index += 1) {
    if (index === source.length || source[index] === '\n') {
      parts.push({ text: source.slice(start, index), styles: styles.slice(start, index) });
      start = index + 1;
    }
  }
  const kept = parts.filter((line, index) => {
    if (line.text.length > 0) return true;
    return index > 0 && index < parts.length - 1;
  });
  return kept
    .map((line) => {
      if (!line.text) return `<w:r>${baseRPr}<w:t xml:space="preserve"></w:t></w:r>`;
      return runsOfLine(line.text, line.styles, baseRPr);
    })
    .join('<w:r><w:br/></w:r>');
}

function runsOfLine(
  text: string,
  styles: readonly (HeadingStyle | null)[],
  baseRPr: string,
): string {
  let xml = '';
  let index = 0;
  while (index < text.length) {
    const style = styles[index] ?? null;
    let end = index + 1;
    while (end < text.length && (styles[end] ?? null) === style) end += 1;
    const props = style ? headingRunProps(baseRPr, style) : baseRPr;
    xml += `<w:r>${props}<w:t xml:space="preserve">${encodeXml(text.slice(index, end))}</w:t></w:r>`;
    index = end;
  }
  return xml;
}

function textRun(text: string, rPr: string): string {
  const clean = stripControls(text);
  if (!clean) return '';
  return clean
    .split('\f')
    .map((chunk, index, all) => {
      const source = index === 0 ? chunk : chunk.replace(/^\n+/, '');
      const lines = source.split('\n').filter((line, lineIndex, parts) => {
        if (line.length > 0) return true;
        return lineIndex > 0 && lineIndex < parts.length - 1;
      });
      const body = lines
        .map((line) => `<w:t xml:space="preserve">${encodeXml(line)}</w:t>`)
        .join('<w:br/>');
      const run = body ? `<w:r>${rPr}${body}</w:r>` : '';
      const page = index < all.length - 1 ? '<w:r><w:br w:type="page"/></w:r>' : '';
      return run + page;
    })
    .join('');
}

function fieldRuns(instruction: 'PAGE' | 'NUMPAGES', rPr: string): string {
  return (
    `<w:r>${rPr}<w:fldChar w:fldCharType="begin" w:dirty="true"/></w:r>` +
    `<w:r>${rPr}<w:instrText xml:space="preserve"> ${instruction} </w:instrText></w:r>` +
    `<w:r>${rPr}<w:fldChar w:fldCharType="separate"/></w:r>` +
    `<w:r>${rPr}<w:t>1</w:t></w:r>` +
    `<w:r>${rPr}<w:fldChar w:fldCharType="end"/></w:r>`
  );
}

function containsKnownToken(text: string, dictionary: PlaceholderDictionary): boolean {
  if (!text.includes('[')) return false;
  for (const token of dictionary.keys()) {
    if (text.includes(token)) return true;
  }
  return false;
}

function paragraphText(paragraph: string): string {
  return [...paragraph.matchAll(TEXT_RE)].map((part) => decodeXml(part[1] ?? '')).join('');
}

function withUpdateFields(xml: string): string {
  if (xml.includes('<w:updateFields')) return xml;
  if (!xml.includes('</w:settings>')) return xml;
  return xml.replace('</w:settings>', '<w:updateFields w:val="true"/></w:settings>');
}

function stripControls(text: string): string {
  let out = '';
  for (const ch of text) {
    const code = ch.charCodeAt(0);
    if (code === 9 || code === 10 || code === 12 || code === 13 || code >= 32) out += ch;
  }
  return out;
}

function encodeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function decodeXml(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, value: string) => String.fromCodePoint(Number(value)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, value: string) => String.fromCodePoint(parseInt(value, 16)))
    .replace(/&amp;/g, '&');
}
